import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TD } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { canWriteMainTab } from "@/lib/maintabAccess";
import {
  LOOKUP_KINDS,
  MODULES,
  designSnapshot,
  emptyField,
  emptyLookup,
  fieldForeignKey,
  flagMark,
  foreignKeyLabel,
  moduleFromPath,
  newTableSpec,
  toSavePayload,
  typeHasLength,
  typeHasNumeric,
  typeLabel,
  typeOptions,
  uniqueFieldName,
  validateTableSpec,
  withFieldKeys,
  yesNo,
} from "@/lib/tableDesigner";
import { getTable, getTableData, listTables, previewSql, saveTable } from "@/lib/tableDesignerApi";
import {
  cellDisplay,
  columnTooltip,
  describeColumns,
  partitionColumns,
  recordSummary,
} from "@/lib/tableDataView";
import FormDesigner from "@/components/formDesigner/FormDesigner";
import "@/styles/emr-table-designer.css";

const COMING_LATER = {
  relationships: "Relationships Designer comes next. Define a foreign key in Advanced field properties for now.",
  queries: "Query Designer is not part of this Table Designer release.",
  reports: "Report Designer is not part of this Table Designer release.",
  application: "Application assembly is not part of this Table Designer release.",
};

function SheetColumnToggle({ presented, checked, onToggle }) {
  return (
    <label className="etd__toggle" title={columnTooltip(presented)}>
      <input type="checkbox" checked={checked} onChange={() => onToggle(presented.databaseColumn)} />
      {presented.displayLabel}
    </label>
  );
}

export default function TableDesignerPage() {
  const { user, isAdmin } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "database-designer", { isAdmin });
  const location = useLocation();
  const navigate = useNavigate();
  const moduleId = moduleFromPath(location.pathname);

  const [tables, setTables] = useState([]);
  const [tableQuery, setTableQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState("public.patients");
  const [table, setTable] = useState(null);
  const [view, setView] = useState("design");
  const [selectedField, setSelectedField] = useState(0);
  const [propsTab, setPropsTab] = useState("general");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [sqlPreview, setSqlPreview] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [sheet, setSheet] = useState(null);
  const [sheetQ, setSheetQ] = useState("");
  const [sheetOffset, setSheetOffset] = useState(0);
  const [sheetColumns, setSheetColumns] = useState(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fieldQuery, setFieldQuery] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [saveErrors, setSaveErrors] = useState([]);

  const filteredTables = useMemo(() => {
    const q = tableQuery.trim().toLowerCase();
    return tables.filter((item) => !q || item.name.includes(q) || (item.description || "").toLowerCase().includes(q));
  }, [tables, tableQuery]);

  const selectedColumn = table?.columns?.[selectedField] || null;
  const readOnlyFields = Boolean(table?.protected);
  const dirty = Boolean(table) && designSnapshot(table) !== savedSnapshot;
  const visibleFields = useMemo(() => {
    const q = fieldQuery.trim().toLowerCase();
    return (table?.columns || [])
      .map((column, index) => ({ column, index }))
      .filter(({ column }) => {
        if (!q) return true;
        return (
          String(column.name || "").toLowerCase().includes(q)
          || String(column.description || "").toLowerCase().includes(q)
          || String(typeLabel(column.dataType) || "").toLowerCase().includes(q)
        );
      });
  }, [fieldQuery, table]);
  const selectedForeignKey = fieldForeignKey(table, selectedColumn?.name);

  // The sheet reads the same way the Form Designer field list does.
  const sheetHeadings = useMemo(() => describeColumns(sheet?.columns), [sheet]);
  const sheetChoices = useMemo(() => partitionColumns(describeColumns(sheet?.availableColumns)), [sheet]);

  const refreshTables = useCallback(async () => {
    const payload = await listTables();
    setTables(payload.items || []);
    return payload.items || [];
  }, []);

  const openTable = useCallback(async (schema, name, nextView = "design") => {
    const payload = await getTable(schema, name);
    const nextTable = { ...payload.table, columns: withFieldKeys(payload.table?.columns) };
    setTable(nextTable);
    setSavedSnapshot(designSnapshot(nextTable));
    setSaveErrors([]);
    setFieldQuery("");
    setSelectedKey(`${schema}.${name}`);
    setSelectedField(0);
    setView(nextView);
    setPropsTab("general");
    setAdvancedOpen(false);
    setSqlPreview("");
    setSheet(null);
    setSheetQ("");
    setSheetOffset(0);
    setSheetColumns(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    refreshTables()
      .then((items) => {
        if (cancelled) return;
        const patients = items.find((item) => item.name === "patients") || items[0];
        if (patients) return openTable(patients.schema, patients.name, "design");
        return null;
      })
      .catch((error) => toast.error(error?.response?.data?.detail || "Could not load tables"));
    return () => {
      cancelled = true;
    };
  }, [openTable, refreshTables]);

  const loadDatasheet = useCallback(
    async (offset = 0, q = sheetQ, columns = sheetColumns) => {
      if (!table?.name) return;
      const payload = await getTableData(table.schema || "public", table.name, {
        limit: 25,
        offset,
        q,
        columns: columns || [],
      });
      setSheet(payload);
      setSheetOffset(offset);
      if (!columns) {
        setSheetColumns((payload.columns || []).map((column) => column.name));
      }
    },
    [sheetColumns, sheetQ, table],
  );

  useEffect(() => {
    if (view !== "datasheet" || !table?.name || table.isNew) return;
    let cancelled = false;
    getTableData(table.schema || "public", table.name, { limit: 25, offset: 0 })
      .then((payload) => {
        if (cancelled) return;
        setSheet(payload);
        setSheetOffset(0);
        setSheetColumns((payload.columns || []).map((column) => column.name));
      })
      .catch((error) => {
        if (!cancelled) toast.error(error?.response?.data?.detail || "Could not load datasheet");
      });
    return () => {
      cancelled = true;
    };
  }, [table?.isNew, table?.name, table?.schema, view]);

  function updateColumn(index, patch) {
    setTable((current) => {
      if (!current) return current;
      const columns = current.columns.map((column, i) => (i === index ? { ...column, ...patch } : column));
      return { ...current, columns };
    });
  }

  function addField() {
    if (!table || table.protected) return;
    const name = uniqueFieldName(table.columns, "new_field");
    setTable((current) => ({ ...current, columns: [...current.columns, emptyField({ name })] }));
    setSelectedField(table.columns.length);
    setView("design");
  }

  function duplicateField() {
    if (!table || table.protected || !selectedColumn) return;
    const { fieldKey: _ignored, lookup, ...rest } = selectedColumn;
    const copy = emptyField({
      ...rest,
      lookup: { ...(lookup || emptyLookup()), options: [...((lookup && lookup.options) || [])] },
      name: uniqueFieldName(table.columns, selectedColumn.name || "new_field"),
      primaryKey: false,
    });
    const nextIndex = selectedField + 1;
    setTable((current) => {
      const columns = [...current.columns];
      columns.splice(nextIndex, 0, copy);
      return { ...current, columns };
    });
    setSelectedField(nextIndex);
    setView("design");
  }

  function deleteField() {
    if (!table || table.protected || !selectedColumn) return;
    if (table.columns.length <= 1) {
      toast.error("A table must keep at least one field");
      return;
    }
    setTable((current) => ({
      ...current,
      columns: current.columns.filter((_, index) => index !== selectedField),
    }));
    setSelectedField(Math.max(0, selectedField - 1));
  }

  function moveField(delta) {
    if (!table || table.protected) return;
    const next = selectedField + delta;
    if (next < 0 || next >= table.columns.length) return;
    setTable((current) => {
      const columns = [...current.columns];
      const [item] = columns.splice(selectedField, 1);
      columns.splice(next, 0, item);
      return { ...current, columns };
    });
    setSelectedField(next);
  }

  async function handleCreateTable() {
    if (!canWrite) return;
    try {
      setBusy(true);
      const spec = newTableSpec(newName.trim(), newDescription.trim());
      const saved = await saveTable(toSavePayload(spec));
      toast.success(`Created ${saved.table.name}`);
      setNewOpen(false);
      setNewName("");
      setNewDescription("");
      await refreshTables();
      await openTable(saved.table.schema, saved.table.name, "design");
      navigate("/database-designer");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create table");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    if (!canWrite || !table) return;
    const check = validateTableSpec(table);
    if (!check.ok) {
      setSaveErrors(check.errors);
      toast.error(check.errors[0]);
      return;
    }
    setSaveErrors([]);
    try {
      setBusy(true);
      const saved = await saveTable(toSavePayload(table));
      toast.success(`Saved ${saved.table.name}`);
      await refreshTables();
      await openTable(saved.table.schema, saved.table.name, view);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save table");
    } finally {
      setBusy(false);
    }
  }

  function updateLookup(patch) {
    if (!selectedColumn) return;
    updateColumn(selectedField, { lookup: { ...(selectedColumn.lookup || emptyLookup()), ...patch } });
  }

  function handleGridKeyDown(event) {
    if (!table) return;
    const target = event.target;
    const inEditor = target && ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName);
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
      event.preventDefault();
      handleSave();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "d") {
      event.preventDefault();
      duplicateField();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "ArrowUp") {
      event.preventDefault();
      moveField(-1);
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "ArrowDown") {
      event.preventDefault();
      moveField(1);
      return;
    }
    if (event.key === "Delete" && !inEditor) {
      event.preventDefault();
      deleteField();
      return;
    }
    if (event.key === "ArrowDown" && !(inEditor && target.tagName === "TEXTAREA")) {
      event.preventDefault();
      setSelectedField((index) => Math.min((table.columns || []).length - 1, index + 1));
      return;
    }
    if (event.key === "ArrowUp" && !(inEditor && target.tagName === "TEXTAREA")) {
      event.preventDefault();
      setSelectedField((index) => Math.max(0, index - 1));
    }
  }

  async function handlePreviewSql() {
    if (!table) return;
    try {
      const payload = await previewSql(toSavePayload(table));
      setSqlPreview(payload.sql || "");
      setAdvancedOpen(true);
      setPropsTab("advanced");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not preview SQL");
    }
  }

  function goModule(id) {
    const match = MODULES.find((item) => item.id === id);
    if (match) navigate(match.path);
  }

  function toggleSheetColumn(name) {
    const current = sheetColumns || [];
    const next = current.includes(name) ? current.filter((item) => item !== name) : [...current, name];
    setSheetColumns(next);
    loadDatasheet(0, sheetQ, next).catch((error) => toast.error(error?.response?.data?.detail || "Could not reload columns"));
  }

  return (
    <div className="etd" data-testid={TD.page}>
      <header className="etd__chrome">
        <h1 className="etd__product">Database Designer</h1>
        <nav className="etd__modules">
          {MODULES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`etd__module${moduleId === item.id ? " is-active" : ""}`}
              data-testid={TD[`module${item.id[0].toUpperCase()}${item.id.slice(1)}`]}
              onClick={() => goModule(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="etd__env" data-testid={TD.environment}>
          Environment: SANDBOX
        </div>
      </header>

      {/* The rail exists to list tables. Other modules would only see it repeat
          the module tabs already in the header, so they get that width for
          their own workspace instead. */}
      <div className={`etd__workspace${moduleId === "tables" ? "" : " etd__workspace--full"}`}>
        {moduleId === "tables" ? (
          <aside className="etd__rail">
            <div className="etd__rail-head">DATABASE</div>
            <div className="etd__rail-nav">
              {MODULES.map((item) => (
                <button
                  key={`rail-${item.id}`}
                  type="button"
                  className={`etd__rail-item${moduleId === item.id ? " is-active" : ""}`}
                  onClick={() => goModule(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <input
              className="etd__search"
              data-testid={TD.tableSearch}
              placeholder="Search tables..."
              value={tableQuery}
              onChange={(event) => setTableQuery(event.target.value)}
            />
            <div className="etd__tables" data-testid={TD.tableList}>
              {filteredTables.map((item) => (
                <button
                  key={`${item.schema}.${item.name}`}
                  type="button"
                  className={`etd__table-item${selectedKey === `${item.schema}.${item.name}` ? " is-active" : ""}`}
                  data-testid={`${TD.tableItem}-${item.name}`}
                  onClick={() => openTable(item.schema, item.name, "design")}
                >
                  {item.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="etd__new-table"
              data-testid={TD.newTableBtn}
              disabled={!canWrite}
              onClick={() => setNewOpen(true)}
            >
              + New Table
            </button>
          </aside>
        ) : null}

        <section className="etd__main">
          {moduleId === "tables" && table ? (
            <>
              <div className="etd__table-head">
                <div>
                  <h2 className="etd__kicker" data-testid={TD.title}>
                    Table Designer
                  </h2>
                  <h3 className="etd__table-title" data-testid={TD.tableName}>
                    {table.name}
                  </h3>
                  <p className="etd__table-copy" data-testid={TD.tableDescription}>
                    {table.description || "No description yet."}
                  </p>
                </div>
                <div className="etd__head-actions">
                  {dirty ? (
                    <span className="etd__unsaved" data-testid={TD.unsaved}>
                      Unsaved changes
                    </span>
                  ) : null}
                  <div className="etd__views">
                    <button
                      type="button"
                      className={`etd__view${view === "design" ? " is-active" : ""}`}
                      data-testid={TD.designViewBtn}
                      onClick={() => setView("design")}
                    >
                      Design View
                    </button>
                    <button
                      type="button"
                      className={`etd__view${view === "datasheet" ? " is-active" : ""}`}
                      data-testid={TD.datasheetViewBtn}
                      disabled={table.isNew}
                      onClick={() => setView("datasheet")}
                    >
                      Datasheet View
                    </button>
                  </div>
                  <button
                    type="button"
                    className="etd__btn"
                    data-testid={TD.previewDataBtn}
                    disabled={table.isNew}
                    onClick={() => setView("datasheet")}
                  >
                    Preview Data
                  </button>
                  <button
                    type="button"
                    className="etd__btn etd__btn--primary"
                    data-testid={TD.saveTableBtn}
                    disabled={!canWrite || busy}
                    onClick={handleSave}
                  >
                    Save Table
                  </button>
                </div>
              </div>
              {saveErrors.length ? (
                <div className="etd__validation" data-testid={TD.validation} role="alert">
                  {saveErrors.map((error) => (
                    <p key={error}>{error}</p>
                  ))}
                </div>
              ) : null}

              {view === "design" ? (
                <div className="etd__design">
                  <div className="etd__grid-wrap" tabIndex={0} onKeyDown={handleGridKeyDown}>
                    <div className="etd__grid-tools">
                      <div className="etd__actions">
                        <button type="button" className="etd__btn" data-testid={TD.addFieldBtn} disabled={!canWrite || table.protected} onClick={addField}>
                          + Add Field
                        </button>
                        <button type="button" className="etd__btn" data-testid={TD.duplicateFieldBtn} disabled={!canWrite || table.protected || !selectedColumn} onClick={duplicateField}>
                          Duplicate Field
                        </button>
                        <button type="button" className="etd__btn etd__btn--ghost" data-testid={TD.deleteFieldBtn} disabled={!canWrite || table.protected} onClick={deleteField}>
                          Delete Field
                        </button>
                        <button type="button" className="etd__btn etd__btn--ghost" data-testid={TD.moveUpBtn} disabled={table.protected} onClick={() => moveField(-1)}>
                          Move Up
                        </button>
                        <button type="button" className="etd__btn etd__btn--ghost" data-testid={TD.moveDownBtn} disabled={table.protected} onClick={() => moveField(1)}>
                          Move Down
                        </button>
                      </div>
                      <input
                        className="etd__search"
                        data-testid={TD.fieldSearch}
                        placeholder="Search fields..."
                        value={fieldQuery}
                        onChange={(event) => setFieldQuery(event.target.value)}
                      />
                    </div>
                    <div className="etd__grid-scroll">
                      <table className="etd__grid" data-testid={TD.fieldGrid}>
                        <thead>
                          <tr>
                            <th className="etd__col-index">#</th>
                            <th className="etd__col-name">Field Name</th>
                            <th className="etd__col-type">Data Type</th>
                            <th className="etd__col-flag">Required</th>
                            <th className="etd__col-flag">PK</th>
                            <th className="etd__col-flag">Unique</th>
                            <th className="etd__col-flag">Indexed</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleFields.map(({ column, index }) => (
                            <tr
                              key={column.fieldKey || `field-${index}`}
                              className={selectedField === index ? "is-selected" : ""}
                              onClick={() => setSelectedField(index)}
                            >
                              <td className="etd__col-index">{index + 1}</td>
                              <td>
                                {readOnlyFields ? (
                                  <span
                                    className="etd__cell-text"
                                    data-testid={`table-designer-field-name-${column.name || index}`}
                                  >
                                    {column.name}
                                  </span>
                                ) : (
                                  <input
                                    type="text"
                                    data-testid={`table-designer-field-name-${column.name || index}`}
                                    value={column.name}
                                    onChange={(event) => updateColumn(index, { name: event.target.value })}
                                  />
                                )}
                              </td>
                              <td>
                                {readOnlyFields ? (
                                  <span
                                    className="etd__cell-text"
                                    data-testid={`table-designer-field-type-${column.name || index}`}
                                  >
                                    {typeLabel(column.dataType)}
                                  </span>
                                ) : (
                                  <select
                                    data-testid={`table-designer-field-type-${column.name || index}`}
                                    value={column.dataType || "text"}
                                    onChange={(event) => updateColumn(index, { dataType: event.target.value })}
                                  >
                                    {typeOptions(column.dataType).map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                              <td className="etd__check">
                                {readOnlyFields ? (
                                  <span
                                    className="etd__flag"
                                    data-testid={`table-designer-field-required-${column.name || index}`}
                                  >
                                    {flagMark(column.required || column.primaryKey)}
                                  </span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    data-testid={`table-designer-field-required-${column.name || index}`}
                                    checked={Boolean(column.required || column.primaryKey)}
                                    onChange={(event) => updateColumn(index, { required: event.target.checked })}
                                  />
                                )}
                              </td>
                              <td className="etd__check">
                                {readOnlyFields ? (
                                  <span
                                    className="etd__flag"
                                    data-testid={`table-designer-field-pk-${column.name || index}`}
                                  >
                                    {flagMark(column.primaryKey)}
                                  </span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    data-testid={`table-designer-field-pk-${column.name || index}`}
                                    checked={Boolean(column.primaryKey)}
                                    onChange={(event) =>
                                      updateColumn(index, {
                                        primaryKey: event.target.checked,
                                        required: event.target.checked || column.required,
                                      })
                                    }
                                  />
                                )}
                              </td>
                              <td className="etd__check">
                                {readOnlyFields ? (
                                  <span
                                    className="etd__flag"
                                    data-testid={`table-designer-field-unique-${column.name || index}`}
                                  >
                                    {flagMark(column.unique)}
                                  </span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    data-testid={`table-designer-field-unique-${column.name || index}`}
                                    checked={Boolean(column.unique)}
                                    onChange={(event) => updateColumn(index, { unique: event.target.checked })}
                                  />
                                )}
                              </td>
                              <td className="etd__check">
                                {readOnlyFields ? (
                                  <span
                                    className="etd__flag"
                                    data-testid={`table-designer-field-indexed-${column.name || index}`}
                                  >
                                    {flagMark(column.indexed)}
                                  </span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    data-testid={`table-designer-field-indexed-${column.name || index}`}
                                    checked={Boolean(column.indexed)}
                                    onChange={(event) => updateColumn(index, { indexed: event.target.checked })}
                                  />
                                )}
                              </td>
                              <td>
                                {readOnlyFields ? (
                                  <span
                                    className="etd__cell-text"
                                    data-testid={`table-designer-field-description-${column.name || index}`}
                                  >
                                    {column.description || ""}
                                  </span>
                                ) : (
                                  <input
                                    type="text"
                                    data-testid={`table-designer-field-description-${column.name || index}`}
                                    value={column.description || ""}
                                    onChange={(event) => updateColumn(index, { description: event.target.value })}
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <aside className="etd__props" data-testid={TD.fieldProps}>
                    <h3>Field Properties</h3>
                    <div className="etd__prop-tabs">
                      <button
                        type="button"
                        className={`etd__prop-tab${propsTab === "general" ? " is-active" : ""}`}
                        data-testid={TD.fieldPropsGeneral}
                        onClick={() => setPropsTab("general")}
                      >
                        General
                      </button>
                      <button
                        type="button"
                        className={`etd__prop-tab${propsTab === "lookup" ? " is-active" : ""}`}
                        data-testid={TD.fieldPropsLookup}
                        onClick={() => setPropsTab("lookup")}
                      >
                        Lookup
                      </button>
                      <button
                        type="button"
                        className={`etd__prop-tab${propsTab === "advanced" ? " is-active" : ""}`}
                        data-testid={TD.fieldPropsAdvanced}
                        onClick={() => setPropsTab("advanced")}
                      >
                        Advanced
                      </button>
                    </div>
                    {selectedColumn && propsTab === "general" && readOnlyFields ? (
                      <>
                        <div className="etd__field">
                          <label>Field Name</label>
                          <span className="etd__prop-value" data-testid={TD.propName}>
                            {selectedColumn.name}
                          </span>
                        </div>
                        <div className="etd__field">
                          <label>Data Type</label>
                          <span className="etd__prop-value" data-testid={TD.propDataType}>
                            {typeLabel(selectedColumn.dataType)}
                          </span>
                        </div>
                        {typeHasLength(selectedColumn.dataType) ? (
                          <div className="etd__field">
                            <label>Size</label>
                            <span className="etd__prop-value" data-testid={TD.propLength}>
                              {selectedColumn.length || "—"}
                            </span>
                          </div>
                        ) : null}
                        {typeHasNumeric(selectedColumn.dataType) ? (
                          <div className="etd__field-row">
                            <div className="etd__field">
                              <label>Precision</label>
                              <span className="etd__prop-value" data-testid={TD.propPrecision}>
                                {selectedColumn.precision || "—"}
                              </span>
                            </div>
                            <div className="etd__field">
                              <label>Scale</label>
                              <span className="etd__prop-value" data-testid={TD.propScale}>
                                {selectedColumn.scale ?? "—"}
                              </span>
                            </div>
                          </div>
                        ) : null}
                        <div className="etd__field">
                          <label>Required</label>
                          <span className="etd__prop-value" data-testid={TD.propRequired}>
                            {yesNo(selectedColumn.required || selectedColumn.primaryKey)}
                          </span>
                        </div>
                        <div className="etd__field">
                          <label>Primary Key</label>
                          <span className="etd__prop-value" data-testid={TD.propPrimaryKey}>
                            {yesNo(selectedColumn.primaryKey)}
                          </span>
                        </div>
                        <div className="etd__field">
                          <label>Unique</label>
                          <span className="etd__prop-value" data-testid={TD.propUnique}>
                            {yesNo(selectedColumn.unique)}
                          </span>
                        </div>
                        <div className="etd__field">
                          <label>Indexed</label>
                          <span className="etd__prop-value" data-testid={TD.propIndexed}>
                            {yesNo(selectedColumn.indexed)}
                          </span>
                        </div>
                        <div className="etd__field">
                          <label>Default Value</label>
                          <span className="etd__prop-value" data-testid={TD.propDefault}>
                            {selectedColumn.defaultValue || "—"}
                          </span>
                        </div>
                        <div className="etd__field">
                          <label>Description</label>
                          <span className="etd__prop-value" data-testid={TD.propDescription}>
                            {selectedColumn.description || "—"}
                          </span>
                        </div>
                      </>
                    ) : null}
                    {selectedColumn && propsTab === "general" && !readOnlyFields ? (
                      <>
                        <div className="etd__field">
                          <label>Field Name</label>
                          <input
                            data-testid={TD.propName}
                            value={selectedColumn.name}
                            onChange={(event) => updateColumn(selectedField, { name: event.target.value })}
                          />
                        </div>
                        <div className="etd__field">
                          <label>Data Type</label>
                          <select
                            data-testid={TD.propDataType}
                            value={selectedColumn.dataType || "text"}
                            onChange={(event) => updateColumn(selectedField, { dataType: event.target.value })}
                          >
                            {typeOptions(selectedColumn.dataType).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {typeHasLength(selectedColumn.dataType) ? (
                          <div className="etd__field">
                            <label>Size</label>
                            <input
                              type="number"
                              min="1"
                              data-testid={TD.propLength}
                              value={selectedColumn.length || ""}
                              onChange={(event) => updateColumn(selectedField, { length: event.target.value ? Number(event.target.value) : null })}
                            />
                          </div>
                        ) : null}
                        {typeHasNumeric(selectedColumn.dataType) ? (
                          <div className="etd__field-row">
                            <div className="etd__field">
                              <label>Precision</label>
                              <input
                                type="number"
                                min="1"
                                data-testid={TD.propPrecision}
                                value={selectedColumn.precision || ""}
                                onChange={(event) => updateColumn(selectedField, { precision: event.target.value ? Number(event.target.value) : null })}
                              />
                            </div>
                            <div className="etd__field">
                              <label>Scale</label>
                              <input
                                type="number"
                                min="0"
                                data-testid={TD.propScale}
                                value={selectedColumn.scale ?? ""}
                                onChange={(event) => updateColumn(selectedField, { scale: event.target.value !== "" ? Number(event.target.value) : null })}
                              />
                            </div>
                          </div>
                        ) : null}
                        <label className="etd__toggle">
                          <input
                            type="checkbox"
                            data-testid={TD.propRequired}
                            checked={Boolean(selectedColumn.required || selectedColumn.primaryKey)}
                            onChange={(event) => updateColumn(selectedField, { required: event.target.checked })}
                          />
                          Required
                        </label>
                        <label className="etd__toggle">
                          <input
                            type="checkbox"
                            data-testid={TD.propPrimaryKey}
                            checked={Boolean(selectedColumn.primaryKey)}
                            onChange={(event) => updateColumn(selectedField, { primaryKey: event.target.checked, required: event.target.checked || selectedColumn.required })}
                          />
                          Primary Key
                        </label>
                        <label className="etd__toggle">
                          <input
                            type="checkbox"
                            data-testid={TD.propUnique}
                            checked={Boolean(selectedColumn.unique)}
                            onChange={(event) => updateColumn(selectedField, { unique: event.target.checked })}
                          />
                          Unique
                        </label>
                        <label className="etd__toggle">
                          <input
                            type="checkbox"
                            data-testid={TD.propIndexed}
                            checked={Boolean(selectedColumn.indexed)}
                            onChange={(event) => updateColumn(selectedField, { indexed: event.target.checked })}
                          />
                          Indexed
                        </label>
                        <div className="etd__field">
                          <label>Default Value</label>
                          <input
                            data-testid={TD.propDefault}
                            value={selectedColumn.defaultValue || ""}
                            onChange={(event) => updateColumn(selectedField, { defaultValue: event.target.value })}
                          />
                        </div>
                        <div className="etd__field">
                          <label>Description</label>
                          <textarea
                            data-testid={TD.propDescription}
                            rows={3}
                            value={selectedColumn.description || ""}
                            onChange={(event) => updateColumn(selectedField, { description: event.target.value })}
                          />
                        </div>
                      </>
                    ) : null}
                    {selectedColumn && propsTab === "lookup" ? (
                      <div>
                        <div className="etd__field">
                          <label>Lookup type</label>
                          {readOnlyFields ? (
                            <span className="etd__prop-value" data-testid={TD.propLookupKind}>
                              {LOOKUP_KINDS.find((item) => item.id === (selectedColumn.lookup?.kind || "none"))?.label || "None"}
                            </span>
                          ) : (
                            <select
                              data-testid={TD.propLookupKind}
                              value={selectedColumn.lookup?.kind || "none"}
                              onChange={(event) => updateLookup({ kind: event.target.value })}
                            >
                              {LOOKUP_KINDS.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        {(selectedColumn.lookup?.kind || "none") === "static" ? (
                          <div className="etd__field">
                            <label>List values</label>
                            {readOnlyFields ? (
                              <span className="etd__prop-value" data-testid={TD.propLookupOptions}>
                                {(selectedColumn.lookup?.options || []).join(", ") || "—"}
                              </span>
                            ) : (
                              <textarea
                                data-testid={TD.propLookupOptions}
                                rows={4}
                                value={(selectedColumn.lookup?.options || []).join("\n")}
                                onChange={(event) =>
                                  updateLookup({
                                    options: event.target.value.split("\n").map((item) => item.trim()).filter(Boolean),
                                  })
                                }
                              />
                            )}
                          </div>
                        ) : null}
                        {(selectedColumn.lookup?.kind || "none") === "reference" ? (
                          <>
                            <div className="etd__field">
                              <label>Related table</label>
                              {readOnlyFields ? (
                                <span className="etd__prop-value" data-testid={TD.propLookupTable}>
                                  {selectedColumn.lookup?.refTable || "—"}
                                </span>
                              ) : (
                                <select
                                  data-testid={TD.propLookupTable}
                                  value={selectedColumn.lookup?.refTable || ""}
                                  onChange={(event) => updateLookup({ refTable: event.target.value })}
                                >
                                  <option value="">Select a table</option>
                                  {tables.map((item) => (
                                    <option key={`${item.schema}.${item.name}`} value={item.name}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <div className="etd__field">
                              <label>Related field</label>
                              {readOnlyFields ? (
                                <span className="etd__prop-value" data-testid={TD.propLookupColumn}>
                                  {selectedColumn.lookup?.refColumn || "id"}
                                </span>
                              ) : (
                                <input
                                  data-testid={TD.propLookupColumn}
                                  value={selectedColumn.lookup?.refColumn || ""}
                                  placeholder="id"
                                  onChange={(event) => updateLookup({ refColumn: event.target.value })}
                                />
                              )}
                            </div>
                            <div className="etd__field">
                              <label>Display field</label>
                              {readOnlyFields ? (
                                <span className="etd__prop-value" data-testid={TD.propLookupDisplay}>
                                  {selectedColumn.lookup?.refDisplay || "—"}
                                </span>
                              ) : (
                                <input
                                  data-testid={TD.propLookupDisplay}
                                  value={selectedColumn.lookup?.refDisplay || ""}
                                  placeholder="name"
                                  onChange={(event) => updateLookup({ refDisplay: event.target.value })}
                                />
                              )}
                            </div>
                          </>
                        ) : null}
                        <div className="etd__field">
                          <label>Database relationship</label>
                          <span className="etd__prop-value" data-testid={TD.propForeignKey}>
                            {foreignKeyLabel(selectedForeignKey) || "None"}
                          </span>
                        </div>
                      </div>
                    ) : null}
                    {selectedColumn && propsTab === "advanced" ? (
                      <div data-testid={TD.advancedToggle}>
                        <p className="etd__lede">Advanced details stay out of the way until you ask.</p>
                        <div className="etd__field">
                          <label>Database column</label>
                          <input value={selectedColumn.name} readOnly />
                        </div>
                        <div className="etd__field">
                          <label>PostgreSQL type</label>
                          <input value={selectedColumn.dataType || typeLabel(selectedColumn.dataType)} readOnly />
                        </div>
                        <div className="etd__field">
                          <label>Foreign key</label>
                          <input
                            placeholder="patients.id"
                            value={foreignKeyLabel(selectedForeignKey)}
                            readOnly
                          />
                        </div>
                        <button type="button" className="etd__btn" data-testid={TD.previewSql} onClick={handlePreviewSql}>
                          SQL Preview
                        </button>
                        {sqlPreview ? <pre className="etd__sql">{sqlPreview}</pre> : null}
                      </div>
                    ) : null}
                  </aside>
                </div>
              ) : (
                <div className="etd__datasheet" data-testid={TD.datasheet}>
                  <div className="etd__sheet-tools">
                    <button type="button" className="etd__btn" data-testid={TD.newRecordBtn} disabled title="Record editing is not part of Table Designer V1">
                      + New Record
                    </button>
                    <input
                      type="search"
                      data-testid={TD.datasheetSearch}
                      placeholder="Search records..."
                      value={sheetQ}
                      onChange={(event) => setSheetQ(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") loadDatasheet(0, event.currentTarget.value).catch(() => {});
                      }}
                    />
                    <div className="etd__columns">
                      <button type="button" className="etd__btn" data-testid={TD.datasheetColumns} onClick={() => setColumnsOpen((open) => !open)}>
                        Columns ▾
                      </button>
                      {columnsOpen ? (
                        <div className="etd__columns-menu">
                          {sheetChoices.everyday.map((presented) => (
                            <SheetColumnToggle
                              key={presented.databaseColumn}
                              presented={presented}
                              checked={(sheetColumns || []).includes(presented.databaseColumn)}
                              onToggle={toggleSheetColumn}
                            />
                          ))}
                          {sheetChoices.technical.length ? (
                            <p className="etd__columns-group" data-testid={TD.datasheetTechnicalGroup}>
                              Technical
                            </p>
                          ) : null}
                          {sheetChoices.technical.map((presented) => (
                            <SheetColumnToggle
                              key={presented.databaseColumn}
                              presented={presented}
                              checked={(sheetColumns || []).includes(presented.databaseColumn)}
                              onToggle={toggleSheetColumn}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <span className="etd__lede" data-testid={TD.datasheetCount}>
                      {recordSummary(sheet)}
                    </span>
                    <button
                      type="button"
                      className="etd__btn"
                      data-testid={TD.datasheetPrev}
                      disabled={!sheet || sheet.offset <= 0}
                      onClick={() => loadDatasheet(Math.max(0, sheetOffset - 25))}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="etd__btn"
                      data-testid={TD.datasheetNext}
                      disabled={!sheet || sheet.offset + (sheet.rows?.length || 0) >= sheet.total}
                      onClick={() => loadDatasheet(sheetOffset + 25)}
                    >
                      ›
                    </button>
                  </div>
                  <div className="etd__sheet-scroll">
                    <table className="etd__sheet-table">
                      <thead>
                        <tr>
                          {sheetHeadings.map((presented) => (
                            <th
                              key={presented.databaseColumn}
                              data-testid={`${TD.sheetHead}-${presented.databaseColumn}`}
                              data-column={presented.databaseColumn}
                              data-lookup={presented.isLookup ? "true" : undefined}
                              className={presented.align === "right" ? "is-right" : undefined}
                              title={columnTooltip(presented)}
                            >
                              {presented.displayLabel}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(sheet?.rows || []).map((row, index) => (
                          <tr key={index} data-testid={`${TD.sheetRow}-${index}`}>
                            {sheetHeadings.map((presented) => {
                              const cell = cellDisplay(
                                row[presented.databaseColumn],
                                presented,
                                (sheet?.lookups || {})[presented.databaseColumn],
                              );
                              return (
                                <td
                                  key={presented.databaseColumn}
                                  data-testid={`${TD.sheetCell}-${index}-${presented.databaseColumn}`}
                                  className={[
                                    cell.tone === "muted" ? "is-muted" : "",
                                    presented.align === "right" ? "is-right" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ") || undefined}
                                  title={cell.title || undefined}
                                >
                                  {cell.text}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {sheet && !(sheet.rows || []).length ? (
                          <tr>
                            <td className="is-muted" colSpan={Math.max(1, sheetHeadings.length)}>
                              {sheet.q ? `No record matches “${sheet.q}”.` : "This table has no records yet."}
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : null}

          {moduleId === "forms" ? <FormDesigner tables={tables} canWrite={canWrite} /> : null}

          {COMING_LATER[moduleId] ? (
            <div className="etd__placeholder" data-testid={TD.placeholder}>
              {COMING_LATER[moduleId]}
            </div>
          ) : null}
        </section>
      </div>

      {newOpen ? (
        <div className="etd__dialog">
          <div className="etd__dialog-card">
            <h2>New Table</h2>
            <div className="etd__field">
              <label>Table Name</label>
              <input data-testid={TD.newTableName} value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="workers_comp_claims" />
            </div>
            <div className="etd__field">
              <label>Description</label>
              <textarea
                data-testid={TD.newTableDescription}
                rows={3}
                value={newDescription}
                onChange={(event) => setNewDescription(event.target.value)}
                placeholder="Workers' Compensation claim information"
              />
            </div>
            <div className="etd__actions">
              <button type="button" className="etd__btn" onClick={() => setNewOpen(false)}>
                Cancel
              </button>
              <button type="button" className="etd__btn etd__btn--primary" data-testid={TD.newTableCreate} disabled={!canWrite || !newName.trim() || busy} onClick={handleCreateTable}>
                Create Table
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
